import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UndoResponse } from '@photos/shared';
import { releasePreview, useReleaseAlbum, useUndo, useUpdatePhotos } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { visibilityToast } from '../../lib/visibility';
import { useToast, type ToastInput } from '../../state/toasts';

const photosN = (n: number) => `${n.toLocaleString('en-US')} ${n === 1 ? 'photo' : 'photos'}`;

/** "Undid 38 of 40; 2 were changed afterwards" */
export function undoMessage(r: UndoResponse): ToastInput {
  return r.skipped
    ? { icon: 'undo', title: `Undid ${r.undone} of ${r.total}`, sub: `${photosN(r.skipped)} ${r.skipped === 1 ? 'was' : 'were'} changed afterwards and stayed as they are.` }
    : { icon: 'undo', title: 'Undone', sub: `${photosN(r.undone)} went back to how they were.` };
}

/** A toast with an Undo button for the activity entry the change created. */
export function useUndoToast() {
  const toast = useToast();
  const undo = useUndo();
  return useCallback(
    (t: ToastInput, activityId: string | null | undefined) => {
      if (!activityId) return toast(t);
      const h = toast({
        ...t,
        sub: (
          <>
            {t.sub}{' '}
            <button
              type="button"
              className="linkbtn"
              onClick={() =>
                undo.mutate(activityId, {
                  onSuccess: (r) => {
                    h.close();
                    toast(undoMessage(r));
                  },
                  onError: (e) => toast({ icon: 'info', title: 'Couldn’t undo', sub: e.message }),
                })
              }
            >
              Undo
            </button>
          </>
        ),
      });
      return h;
    },
    [toast, undo],
  );
}

type ReleaseTarget = { ids: string[]; albumId?: string } | { album: { id: string; name: string } };

/**
 * Release confirmation: "Release 128 photos? 12 of them are also in other albums." Releases the selected unreleased
 * photos, or every unreleased photo of an album.
 */
export function ReleaseDialog({ target, onClose, onDone }: { target: ReleaseTarget; onClose(): void; onDone?(): void }) {
  const preview = useQuery({
    queryKey: ['studio', 'release-preview', target],
    queryFn: () => releasePreview('album' in target ? { albumId: target.album.id } : { ids: target.ids }),
    staleTime: 0,
  });
  const update = useUpdatePhotos();
  const releaseAlbum = useReleaseAlbum();
  const undoToast = useUndoToast();
  const toast = useToast();
  const n = preview.data?.count ?? 0;
  const busy = update.isPending || releaseAlbum.isPending;

  const done = (count: number, activityId: string | null | undefined) => {
    onClose();
    undoToast(visibilityToast('released', count), activityId);
    onDone?.();
  };
  const fail = (e: Error) => toast({ icon: 'info', title: 'Couldn’t release', sub: e.message });
  const confirm = () => {
    if ('album' in target) releaseAlbum.mutate(target.album.id, { onSuccess: (r) => done(r.changed, r.activityId), onError: fail });
    else
      update.mutate(
        { ids: target.ids, albumId: target.albumId, visibility: 'released' },
        { onSuccess: (r) => done(r.items.length, r.activityIds[0]), onError: fail },
      );
  };

  return (
    <Modal onClose={onClose} labelledBy="release-title">
      <h3 id="release-title">{preview.data ? (n ? `Release ${photosN(n)}?` : 'Nothing to release') : 'Release photos'}</h3>
      {preview.isError ? (
        <p className="help">{preview.error.message}</p>
      ) : !preview.data ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : (
        <p className="help">
          {n
            ? `${preview.data.alsoInOtherAlbums ? `${preview.data.alsoInOtherAlbums.toLocaleString('en-US')} of them ${preview.data.alsoInOtherAlbums === 1 ? 'is' : 'are'} also in other albums. ` : ''}Everyone signed in will be able to see ${n === 1 ? 'it' : 'them'} in Photos.`
            : 'There are no unreleased photos here.'}
          {'album' in target && n > 0 && ' Restricted photos stay restricted.'}
        </p>
      )}
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={!n || busy} onClick={confirm}>
          {n ? `Release ${photosN(n)}` : 'Release'}
        </button>
      </div>
    </Modal>
  );
}
